import { Line } from '@react-three/drei';
import { ThreeEvent } from '@react-three/fiber';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { Group } from 'three';

import { PartitionWallProps } from '../../@types/Components';
import { EDraggablePointType, EInteractionMode, EUnitType, EWall, EWallMode } from '../../@types/Enums';
import useDraggableMesh from '../../hooks/useDraggableMesh';
import useWallDragging from '../../hooks/useWallDragging';
import { IWall } from '../../models/abstract/Wall';
import DoorModel from '../../models/standard/DoorModel';
import PassageModel from '../../models/standard/PassageModel';
import PartitionWallModel from '../../models/standard/PartitionWallModel';
import RadiatorModel from '../../models/standard/RadiatorModel';
import WindowModel from '../../models/standard/WindowModel';
import { useBuilding } from '../../provider/BuildingProvider/BuildingProvider';
import { useDraggingContext } from '../../provider/DraggingProvider';
import { useMultiSelectToggleContext } from '../../provider/MultiSelectToggleProvider';
import { usePlan } from '../../provider/PlanProvider';
import { useToolbarContext } from '../../provider/ToolbarProvider';
import { useUnitContext } from '../../provider/UnitProvider';
import { fetchAttributesForObject } from '../../utils/attributes/attributesForObjectLoader';
import { DefaultAttributeId, ObjectType } from '../../utils/attributes/defaultObjectsAndAttributes';
import { DEFAULT_RADIATOR_DEPTH, DEFAULT_RADIATOR_LENGTH, INTERNAL_TO_CM_FACTOR, UNIT_TO_METER_DIVISOR } from '../../utils/constants';
import { convertTo } from '../../utils/conversionUtils';
import RENDER_ORDERS from '../../utils/renderOrder';
import { calculateWallLength, distanceToWallNormal, getAngleFromPoints } from '../../utils/vectorUtils';

import { Side } from '../../@types/Side';
import Door, { snapDoorToWall } from './Door';
import DraggablePoint from './DraggablePoint';
import ElementDistanceIndicator from './indicators/ElementDistanceIndicator';
import WallLengthIndicator from './indicators/WallLengthIndicator';
import Passage, { snapPassageToWall } from './Passage';
import Radiator, { snapRadiatorToWall } from './Radiator';
import TempRadiator from './TempRadiator';
import Window, { snapWindowToWall } from './Window';

const MemoizedDoor = React.memo(Door);
const MemoizedPassage = React.memo(Passage);
const MemoizedWindow = React.memo(Window);
const MemoizedRadiator = React.memo(Radiator);

interface DragSnapshot {
  startPoint: THREE.Vector3;
  endPoint: THREE.Vector3;
  windows: THREE.Vector3[];
  doors: THREE.Vector3[];
  passages: THREE.Vector3[];
  radiatorsA: THREE.Vector3[];
  radiatorsB: THREE.Vector3[];
}

const PartitionWall: React.FC<PartitionWallProps> = ({
  onClick,
  selectedWall,
  index,
  onWallUpdate,
  camera,
  raycaster,
  planeRef,
  onDragPreview,
  onWindowClick,
  onDoorClick,
  onPassageClick,
  onRadiatorClick,
  selectedWindow,
  selectedDoor,
  selectedPassage,
  selectedRadiator,
}) => {
  const [hovered, setHovered] = useState<boolean>(false);
  const [tempPoint, setTempPoint] = useState<THREE.Vector3 | null>(null);
  const [isHoveringEndpoint, setIsHoveringEndpoint] = useState(false);

  const { building, setPartitionWallGeometryAndTypeAttributes, updateDoorAttributes, updatePassageAttributes, updateWindowAttributes, updateRadiatorAttributes, getGermanDoorDirectionText } = useBuilding();
  const { interactionMode, wallMode } = useToolbarContext();
  const { isDragging } = useDraggingContext();
  const { showWallInnerLength, showElementDistances } = useMultiSelectToggleContext();
  const { unitType } = useUnitContext();
  const { planModel } = usePlan();

  const groupRef = useRef<Group>(null);
  const snapshotRef = useRef<DragSnapshot | null>(null);
  const initialMousePositionRef = useRef<THREE.Vector3 | null>(null);

  const isDraggingRef = useRef(isDragging);
  const prevIsDraggingRef = useRef(isDragging);
  const lastGeomKeyRef = useRef<string>('');

  const isEndpointDraggingRef = useRef(false);
  const wasMeshDraggingRef = useRef(false);
  const lastPreviewKeyRef = useRef<string | null>(null);

  const wallModel = building.walls[index] as PartitionWallModel | undefined;

  const regionCode = planModel?.region ? String(planModel.region).toUpperCase() : 'DE';
  const unit = unitType === EUnitType.METRIC ? 'cm' : 'in';

  // NEU: wallNormal freigelegt für den Indikator
  const { pC, pD, pC2, pD2, shape, wallLength, wallNormal } = useMemo(() => {
    if (!wallModel) return { pC: null, pD: null, pC2: null, pD2: null, shape: null, wallLength: 0, wallNormal: null };
    const start = wallModel.startPoint;
    const end = wallModel.endPoint;
    const depth = wallModel.depth;

    if (start.equals(end)) return { pC: null, pD: null, pC2: null, pD2: null, shape: null, wallLength: 0, wallNormal: null };

    const dir = new THREE.Vector3().subVectors(end, start).normalize();
    const normal = new THREE.Vector3(-dir.y, dir.x, 0).normalize();
    const halfDepth = depth / 2;

    const pointC2 = start.clone().add(normal.clone().multiplyScalar(-halfDepth));
    const pointD2 = end.clone().add(normal.clone().multiplyScalar(-halfDepth));
    const pointC = start.clone().add(normal.clone().multiplyScalar(halfDepth));
    const pointD = end.clone().add(normal.clone().multiplyScalar(halfDepth));

    const newShape = new THREE.Shape();
    newShape.moveTo(pointC2.x, -pointC2.y);
    newShape.lineTo(pointD2.x, -pointD2.y);
    newShape.lineTo(pointD.x, -pointD.y);
    newShape.lineTo(pointC.x, -pointC.y);
    newShape.closePath();

    const length = calculateWallLength(start, end) / UNIT_TO_METER_DIVISOR;

    return { pC: pointC, pD: pointD, pC2: pointC2, pD2: pointD2, shape: newShape, wallLength: length, wallNormal: normal };
  }, [wallModel?.startPoint.x, wallModel?.startPoint.y, wallModel?.endPoint.x, wallModel?.endPoint.y, wallModel?.depth]);

  useEffect(() => {
    if (wallModel && pC && pD && pC2 && pD2) {
      (wallModel as any).outerStartPoint = pC;
      (wallModel as any).outerEndPoint = pD;
      (wallModel as any).outerStartPoint2 = pC2;
      (wallModel as any).outerEndPoint2 = pD2;
    }
  }, [wallModel, pC, pD, pC2, pD2]);

  const geometry = useMemo(() => {
    if (!shape) return undefined;
    const geom = new THREE.ShapeGeometry(shape);
    geom.rotateX(Math.PI / 2);
    return geom;
  }, [shape]);

  const hatchMaterial = useMemo(() => {
    return new THREE.ShaderMaterial({
      extensions: { derivatives: true },
      uniforms: {
        uBaseColor: { value: new THREE.Color('#ffffff') },
        uHatchColor: { value: new THREE.Color('#111111') },
        uSpacing: { value: 1.2 },
        uThickness: { value: 0.2 },
      },
      vertexShader: `
        varying vec2 vWorldPos;
        void main() {
          vec4 worldPosition = modelMatrix * vec4(position, 1.0);
          vWorldPos = worldPosition.xy;
          gl_Position = projectionMatrix * viewMatrix * worldPosition;
        }
      `,
      fragmentShader: `
      varying vec2 vWorldPos;
      uniform vec3 uBaseColor;
      uniform vec3 uHatchColor;
      uniform float uSpacing;
      uniform float uThickness;
      void main() {
        float coord = vWorldPos.x - vWorldPos.y;
        float halfSpacing = uSpacing / 2.0;
        float dist = abs(mod(coord + halfSpacing, uSpacing) - halfSpacing);
        float halfThick = uThickness / 2.0;
        float f = length(vec2(dFdx(coord), dFdy(coord)));
        float edge = smoothstep(halfThick - f, halfThick + f, dist);
        vec3 lineColor = mix(uHatchColor, uBaseColor, edge);
        float averageCoverage = uThickness / uSpacing;
        vec3 averageColor = mix(uBaseColor, uHatchColor, averageCoverage);
        float fade = smoothstep(uSpacing * 0.15, uSpacing * 0.6, f);
        gl_FragColor = vec4(mix(lineColor, averageColor, fade), 1.0);
      }
    `,
      side: THREE.DoubleSide,
    });
  }, []);

  const partitionFilteredIndex = useMemo(() => {
    const partIndices = building.walls.map((w, i) => (w?.type === EWall.PARTITION ? i : -1)).filter((i) => i !== -1);
    return partIndices.findIndex((gi) => gi === index);
  }, [building.walls, index]);

  const isSelected = selectedWall.wallIndex === partitionFilteredIndex;
  const geomKey = (w: PartitionWallModel | undefined, ut: any) => (w ? `${ut}|${w.startPoint.x},${w.startPoint.y}|${w.endPoint.x},${w.endPoint.y}|${w.depth}` : 'nil');

  const meshColor = useMemo(() => {
    if (!wallModel) return '#444444';
    if (isSelected) return '#009fe3';
    if (hovered) return '#565656';
    return '#111111';
  }, [hovered, isSelected, wallModel]);

  useEffect(() => {
    const rawColor = new THREE.Color(meshColor);
    rawColor.convertLinearToSRGB();
    hatchMaterial.uniforms.uHatchColor.value.copy(rawColor);
  }, [meshColor, hatchMaterial]);

  useEffect(() => {
    isDraggingRef.current = isDragging;
  }, [isDragging]);

  useEffect(() => {
    if (!wallModel) return;

    const key = geomKey(wallModel, unitType);
    const dragEnded = prevIsDraggingRef.current && !isDragging;
    prevIsDraggingRef.current = isDragging;

    const needsAttributes = !wallModel.attributes || wallModel.attributes.length === 0;
    const geometryChanged = key !== lastGeomKeyRef.current;

    if ((dragEnded || geometryChanged || needsAttributes) && !isDragging) {
      lastGeomKeyRef.current = key;
      if (partitionFilteredIndex >= 0) {
        setPartitionWallGeometryAndTypeAttributes(partitionFilteredIndex, unitType);
      }
    }
  }, [wallModel, isDragging, unitType, partitionFilteredIndex, setPartitionWallGeometryAndTypeAttributes, wallModel?.attributes]);

  const { setDraggingPosition } = useWallDragging(raycaster, camera, planeRef, onWallUpdate, wallModel as IWall, building.walls);

  const handleStartEndpointDrag = useCallback(
    (...args: Parameters<typeof setDraggingPosition>) => {
      isEndpointDraggingRef.current = true;
      if (wallModel && onDragPreview) {
        lastPreviewKeyRef.current = null;
        onDragPreview('start', wallModel.startPoint.clone(), wallModel.endPoint.clone());
      }
      setDraggingPosition(...args);
    },
    [setDraggingPosition, wallModel, onDragPreview],
  );

  const handlePointerEvents = (event: ThreeEvent<PointerEvent>): void => {
    event.stopPropagation();
    if (interactionMode !== EInteractionMode.MOVE && onClick) onClick();
  };

  const snapPartitionWall = useCallback((newPosition: THREE.Vector3): THREE.Vector3 => {
    newPosition.z = 0;
    return newPosition;
  }, []);

  const handleFreeWallDrag = useCallback(
    (newPos: THREE.Vector3) => {
      if (!wallModel) return;
      if (isDraggingRef.current) return;
      if (isEndpointDraggingRef.current) return;

      if (!snapshotRef.current) {
        snapshotRef.current = {
          startPoint: wallModel.startPoint.clone(),
          endPoint: wallModel.endPoint.clone(),
          windows: (wallModel.windows || []).map((w) => w.position.clone()),
          doors: (wallModel.doors || []).map((d) => d.position.clone()),
          passages: (wallModel.passages || []).map((p) => p.position.clone()),
          radiatorsA: (wallModel.sides?.A?.radiators || []).map((r) => r.position.clone()),
          radiatorsB: (wallModel.sides?.B?.radiators || []).map((r) => r.position.clone()),
        };
        initialMousePositionRef.current = newPos.clone();
      }

      if (!initialMousePositionRef.current || !snapshotRef.current) return;

      const displacement = new THREE.Vector3().subVectors(newPos, initialMousePositionRef.current);
      const base = snapshotRef.current;

      wallModel.startPoint.copy(base.startPoint).add(displacement);
      wallModel.endPoint.copy(base.endPoint).add(displacement);

      if (wallModel.windows) {
        wallModel.windows = wallModel.windows.map((w, i) => {
          const initialPos = base.windows[i];
          if (!initialPos) return w;
          const newW = Object.assign(Object.create(Object.getPrototypeOf(w)), w);
          newW.position = initialPos.clone().add(displacement);
          return newW;
        });
      }

      if (wallModel.doors) {
        wallModel.doors = wallModel.doors.map((d, i) => {
          const initialPos = base.doors[i];
          if (!initialPos) return d;
          const newD = Object.assign(Object.create(Object.getPrototypeOf(d)), d);
          newD.position = initialPos.clone().add(displacement);
          return newD;
        });
      }

      if (wallModel.passages) {
        wallModel.passages = wallModel.passages.map((p, i) => {
          const initialPos = base.passages[i];
          if (!initialPos) return p;
          const newP = Object.assign(Object.create(Object.getPrototypeOf(p)), p);
          newP.position = initialPos.clone().add(displacement);
          return newP;
        });
      }

      if (wallModel.sides) {
        if (wallModel.sides.A?.radiators) {
          wallModel.sides.A.radiators = wallModel.sides.A.radiators.map((r, i) => {
            const initialPos = base.radiatorsA[i];
            if (!initialPos) return r;
            const newR = Object.assign(Object.create(Object.getPrototypeOf(r)), r);
            newR.position = initialPos.clone().add(displacement);
            return newR;
          });
        }
        if (wallModel.sides.B?.radiators) {
          wallModel.sides.B.radiators = wallModel.sides.B.radiators.map((r, i) => {
            const initialPos = base.radiatorsB[i];
            if (!initialPos) return r;
            const newR = Object.assign(Object.create(Object.getPrototypeOf(r)), r);
            newR.position = initialPos.clone().add(displacement);
            return newR;
          });
        }
      }

      onWallUpdate(wallModel.uuid, wallModel);

      if (onDragPreview && wasMeshDraggingRef.current) {
        onDragPreview('move', wallModel.startPoint.clone(), wallModel.endPoint.clone());
      }
    },
    [wallModel, onWallUpdate, onDragPreview],
  );

  const { isDragging: isMeshDragging } = useDraggableMesh(groupRef, snapPartitionWall, handleFreeWallDrag, interactionMode !== EInteractionMode.MOVE && !isHoveringEndpoint, false);

  useEffect(() => {
    if (!wallModel || !onDragPreview) return;

    if (isMeshDragging && !wasMeshDraggingRef.current) {
      wasMeshDraggingRef.current = true;
      if (onDragPreview) onDragPreview('start', wallModel.startPoint.clone(), wallModel.endPoint.clone());
    }

    if (!isMeshDragging && wasMeshDraggingRef.current) {
      wasMeshDraggingRef.current = false;
      snapshotRef.current = null;
      initialMousePositionRef.current = null;
      if (onDragPreview) onDragPreview('end', wallModel.startPoint.clone(), wallModel.endPoint.clone());
    }
  }, [isMeshDragging, wallModel, onDragPreview]);

  useEffect(() => {
    if (!isMeshDragging) {
      initialMousePositionRef.current = null;
      snapshotRef.current = null;
    }
  }, [isMeshDragging]);

  useEffect(() => {
    if (!wallModel || !onDragPreview) return;
    if (!isEndpointDraggingRef.current) return;

    const key = geomKey(wallModel, unitType);
    if (key === lastPreviewKeyRef.current) return;
    lastPreviewKeyRef.current = key;

    onDragPreview('move', wallModel.startPoint.clone(), wallModel.endPoint.clone());
  }, [wallModel, wallModel?.startPoint.x, wallModel?.startPoint.y, wallModel?.endPoint.x, wallModel?.endPoint.y, unitType, onDragPreview]);

  useEffect(() => {
    if (!onDragPreview || !wallModel) return;
    if (!isDragging && isEndpointDraggingRef.current) {
      isEndpointDraggingRef.current = false;
      lastPreviewKeyRef.current = null;
      onDragPreview('end', wallModel.startPoint.clone(), wallModel.endPoint.clone());
    }
  }, [isDragging, onDragPreview, wallModel]);

  const handleWallMeshClick = (e: ThreeEvent<PointerEvent>): void => {
    if (!wallModel || !pC || !pD || !pC2 || !pD2) return;

    if (wallMode === EWallMode.WINDOW) {
      const windowModel = new WindowModel(snapWindowToWall(wallModel, e.point), undefined, wallModel.depth, getAngleFromPoints(wallModel.startPoint, wallModel.endPoint));
      (wallModel.windows || (wallModel.windows = [])).push(windowModel);
      onWallUpdate(wallModel.uuid, wallModel);

      fetchAttributesForObject(windowModel, ObjectType.Window, regionCode).then((attrs) => {
        const widthCm = windowModel.length * INTERNAL_TO_CM_FACTOR;
        const widthValue = unitType === EUnitType.METRIC ? parseFloat(widthCm.toFixed(3)) : Math.round(convertTo(widthCm, 'cm' as any, 'inches' as any));
        const widthAttr = attrs.find((attr) => attr.id === DefaultAttributeId.WINDOW_WIDTH);
        if (widthAttr) {
          widthAttr.value = widthValue;
          widthAttr.unit = unit;
        }
        updateWindowAttributes(index, (wallModel.windows?.length ?? 1) - 1, attrs);
      });
    } else if (wallMode === EWallMode.DOOR) {
      const doorModel = new DoorModel(snapDoorToWall(wallModel, e.point), undefined, wallModel.depth, getAngleFromPoints(wallModel.startPoint, wallModel.endPoint));
      (wallModel.doors || (wallModel.doors = [])).push(doorModel);
      onWallUpdate(wallModel.uuid, wallModel);

      fetchAttributesForObject(doorModel, ObjectType.Door, regionCode).then((attrs) => {
        const widthCm = doorModel.length * INTERNAL_TO_CM_FACTOR;
        const widthValue = unitType === EUnitType.METRIC ? parseFloat(widthCm.toFixed(3)) : Math.round(convertTo(widthCm, 'cm' as any, 'inches' as any));
        const widthAttr = attrs.find((attr) => attr.id === DefaultAttributeId.DOOR_WIDTH);
        if (widthAttr) {
          widthAttr.value = widthValue;
          widthAttr.unit = unit;
        }
        const openingDirAttr = attrs.find((attr) => attr.id === DefaultAttributeId.DOOR_OPENING_DIRECTION);
        if (openingDirAttr) {
          openingDirAttr.value = getGermanDoorDirectionText(doorModel.openingDirection, doorModel.orientation);
        }
        updateDoorAttributes(index, (wallModel.doors?.length ?? 1) - 1, attrs);
      });
    } else if (wallMode === EWallMode.PASSAGE) {
      const passageModel = new PassageModel(snapPassageToWall(wallModel, e.point), undefined, wallModel.depth, getAngleFromPoints(wallModel.startPoint, wallModel.endPoint));
      (wallModel.passages || (wallModel.passages = [])).push(passageModel);
      onWallUpdate(wallModel.uuid, wallModel);

      fetchAttributesForObject(passageModel, ObjectType.Passage, regionCode).then((attrs) => {
        const widthCm = passageModel.length * INTERNAL_TO_CM_FACTOR;
        const widthValue = unitType === EUnitType.METRIC ? parseFloat(widthCm.toFixed(3)) : Math.round(convertTo(widthCm, 'cm' as any, 'inches' as any));
        const widthAttr = attrs.find((attr) => attr.id === DefaultAttributeId.PASSAGE_WIDTH);
        if (widthAttr) {
          widthAttr.value = widthValue;
          widthAttr.unit = unit;
        }
        updatePassageAttributes(index, (wallModel.passages?.length ?? 1) - 1, attrs);
      });
    } else if (wallMode === EWallMode.RADIATOR) {
      const distA = distanceToWallNormal(e.point, pC2, pD2);
      const distB = distanceToWallNormal(e.point, pC, pD);
      const side: Side = distA <= distB ? 'A' : 'B';

      const radiatorPosition = snapRadiatorToWall(wallModel, e.point, DEFAULT_RADIATOR_LENGTH, DEFAULT_RADIATOR_DEPTH, 0, side);
      const radiatorAngle = getAngleFromPoints(wallModel.startPoint, wallModel.endPoint);

      const radiatorModel = new RadiatorModel(radiatorPosition, DEFAULT_RADIATOR_LENGTH, DEFAULT_RADIATOR_DEPTH, radiatorAngle);

      if (!wallModel.sides) {
        (wallModel as any).sides = { A: { radiators: [] }, B: { radiators: [] } };
      }
      if (!wallModel.sides[side]) {
        wallModel.sides[side] = { radiators: [] };
      }

      const newRadiatorIndex = wallModel.sides[side].radiators.length;
      wallModel.sides[side].radiators = [...wallModel.sides[side].radiators, radiatorModel];

      onWallUpdate(wallModel.uuid, wallModel);

      fetchAttributesForObject(radiatorModel, ObjectType.Radiator, regionCode).then((attrs) => {
        const lengthCm = radiatorModel.length * INTERNAL_TO_CM_FACTOR;
        const depthCm = radiatorModel.depth * INTERNAL_TO_CM_FACTOR;
        const lengthValue = unitType === EUnitType.METRIC ? parseFloat(lengthCm.toFixed(3)) : Math.round(convertTo(lengthCm, 'cm' as any, 'inches' as any));
        const depthValue = unitType === EUnitType.METRIC ? parseFloat(depthCm.toFixed(3)) : Math.round(convertTo(depthCm, 'cm' as any, 'inches' as any));
        const lengthAttr = attrs.find((attr) => attr.id === DefaultAttributeId.RADIATOR_LENGTH);
        if (lengthAttr) {
          lengthAttr.value = lengthValue;
          lengthAttr.unit = unit;
        }
        const depthAttr = attrs.find((attr) => attr.id === DefaultAttributeId.RADIATOR_DEPTH);
        if (depthAttr) {
          depthAttr.value = depthValue;
          depthAttr.unit = unit;
        }
        updateRadiatorAttributes(index, side, newRadiatorIndex, attrs);
      });
    } else if (onClick && interactionMode !== EInteractionMode.MOVE) {
      onClick();
    }
  };

  useEffect(() => {
    if (
      !wallModel ||
      ((wallModel.windows?.length ?? 0) === 0 &&
        (wallModel.doors?.length ?? 0) === 0 &&
        (wallModel.passages?.length ?? 0) === 0 &&
        (wallModel.sides?.A?.radiators?.length ?? 0) === 0 &&
        (wallModel.sides?.B?.radiators?.length ?? 0) === 0)
    ) {
      return;
    }

    let elementsChanged = false;

    const prevRadiatorsA = (wallModel.sides?.A?.radiators || []).map((r) => r.position.toArray().join(','));
    const prevRadiatorsB = (wallModel.sides?.B?.radiators || []).map((r) => r.position.toArray().join(','));
    const angle = getAngleFromPoints(wallModel.startPoint, wallModel.endPoint);

    // --- WINDOWS ---
    wallModel.windows = (wallModel.windows || []).map((window) => {
      const currentPos = (window.position as any)?.clone ? (window.position as THREE.Vector3).clone() : new THREE.Vector3(...(window.position as any));
      const snappedPos = snapWindowToWall(wallModel, currentPos);

      if (currentPos.distanceTo(snappedPos) > 0.0001 || window.depth !== wallModel.depth || window.angle !== angle) {
        window.position = snappedPos;
        window.angle = angle;
        window.depth = wallModel.depth;
        elementsChanged = true;
      }
      return window;
    });

    // --- DOORS ---
    wallModel.doors = (wallModel.doors || []).map((door) => {
      const currentPos = (door.position as any)?.clone ? (door.position as THREE.Vector3).clone() : new THREE.Vector3(...(door.position as any));
      const snappedPos = snapDoorToWall(wallModel, currentPos);

      if (currentPos.distanceTo(snappedPos) > 0.0001 || door.depth !== wallModel.depth || door.angle !== angle) {
        door.position = snappedPos;
        door.angle = angle;
        door.depth = wallModel.depth;
        elementsChanged = true;
      }
      return door;
    });

    // --- PASSAGES ---
    wallModel.passages = (wallModel.passages || []).map((passage) => {
      const currentPos = (passage.position as any)?.clone ? (passage.position as THREE.Vector3).clone() : new THREE.Vector3(...(passage.position as any));
      const snappedPos = snapPassageToWall(wallModel, currentPos);

      if (currentPos.distanceTo(snappedPos) > 0.0001 || passage.depth !== wallModel.depth || passage.angle !== angle) {
        passage.position = snappedPos;
        passage.angle = angle;
        passage.depth = wallModel.depth;
        elementsChanged = true;
      }
      return passage;
    });

    // --- RADIATORS ---
    const updateRadiatorsForSide = (side: 'A' | 'B') => {
      if (!wallModel.sides?.[side]) return;

      wallModel.sides[side].radiators = (wallModel.sides[side].radiators || []).map((radiator) => {
        const currentPosition = (radiator.position as any)?.clone ? (radiator.position as THREE.Vector3).clone() : new THREE.Vector3(...(radiator.position as any));
        const updatedPosition = snapRadiatorToWall(wallModel, currentPosition, radiator.length, radiator.depth, radiator.nicheDepth ?? 0, side);

        if (currentPosition.distanceTo(updatedPosition) < 0.0001 && radiator.angle === angle) {
          return radiator;
        }

        const updated = new RadiatorModel(updatedPosition, radiator.length, radiator.depth, angle, radiator.attributes, undefined, radiator.nicheDepth ?? 0);
        updated.setUUID(radiator.uuid);
        return updated;
      });
    };

    updateRadiatorsForSide('A');
    updateRadiatorsForSide('B');

    const hasRadAChanged = JSON.stringify(prevRadiatorsA) !== JSON.stringify((wallModel.sides?.A?.radiators || []).map((r) => r.position.toArray().join(',')));
    const hasRadBChanged = JSON.stringify(prevRadiatorsB) !== JSON.stringify((wallModel.sides?.B?.radiators || []).map((r) => r.position.toArray().join(',')));

    // Trigger update if anything changed
    if (!isDragging && (elementsChanged || hasRadAChanged || hasRadBChanged)) {
      onWallUpdate(wallModel.uuid, wallModel);
    }
  }, [wallModel?.uuid, wallModel?.depth, wallModel?.startPoint?.x, wallModel?.startPoint?.y, wallModel?.endPoint?.x, wallModel?.endPoint?.y, isDragging, onWallUpdate]);

  const outlineColor = isSelected ? '#009fe3' : hovered ? '#565656' : '#222222';
  const outlineWidth = 3;
  const lineZ = 0.03;
  const meshZ = 0.02;

  const hasStartNeighbor = useMemo(() => {
    return building.walls.some((w, i) => i !== index && (w.startPoint.distanceTo(wallModel!.startPoint) < 0.1 || w.endPoint.distanceTo(wallModel!.startPoint) < 0.1));
  }, [building.walls, wallModel, index, wallModel?.startPoint.x, wallModel?.startPoint.y]);

  const hasEndNeighbor = useMemo(() => {
    return building.walls.some((w, i) => i !== index && (w.startPoint.distanceTo(wallModel!.endPoint) < 0.1 || w.endPoint.distanceTo(wallModel!.endPoint) < 0.1));
  }, [building.walls, wallModel, index, wallModel?.endPoint.x, wallModel?.endPoint.y]);

  return (
    <group ref={groupRef} name={`group-partitionWall#${index}`} renderOrder={RENDER_ORDERS.wall}>
      {geometry && (
        <mesh
          name={'partitionWall-mesh'}
          key={index}
          geometry={geometry}
          position={[0, 0, meshZ]}
          rotation={[Math.PI / 2, 0, 0]}
          onPointerDown={(e) => {
            if (e.button !== 0) return;
            if (wallMode === EWallMode.NONE) {
              handlePointerEvents(e);
            } else {
              handleWallMeshClick(e);
            }
          }}
          onPointerOver={(e) => {
            e.stopPropagation();
            if (!isDragging) setHovered(true);
          }}
          onPointerOut={() => {
            setHovered(false);
            setTempPoint(null);
          }}
          onPointerMove={(e) => {
            if (wallMode === EWallMode.RADIATOR) setTempPoint(e.point.clone());
          }}
        >
          <primitive object={hatchMaterial} attach="material" />
        </mesh>
      )}

      {pC && pD && pC2 && pD2 && (
        <>
          <Line points={[new THREE.Vector3(pC.x, pC.y, lineZ), new THREE.Vector3(pD.x, pD.y, lineZ)]} color={outlineColor} lineWidth={outlineWidth} />
          <Line points={[new THREE.Vector3(pC2.x, pC2.y, lineZ), new THREE.Vector3(pD2.x, pD2.y, lineZ)]} color={outlineColor} lineWidth={outlineWidth} />
          {!hasStartNeighbor && <Line points={[new THREE.Vector3(pC.x, pC.y, lineZ), new THREE.Vector3(pC2.x, pC2.y, lineZ)]} color={outlineColor} lineWidth={outlineWidth} />}
          {!hasEndNeighbor && <Line points={[new THREE.Vector3(pD.x, pD.y, lineZ), new THREE.Vector3(pD2.x, pD2.y, lineZ)]} color={outlineColor} lineWidth={outlineWidth} />}
        </>
      )}

      {wallModel && wallMode === EWallMode.RADIATOR && hovered && tempPoint && (
        <TempRadiator wall={wallModel} pointer={tempPoint} width={DEFAULT_RADIATOR_LENGTH} thickness={DEFAULT_RADIATOR_DEPTH} nicheDepth={0} />
      )}

      {wallModel &&
        (wallModel.windows || []).map((window, idx) => (
          <MemoizedWindow
            key={`pw-window-${idx}`}
            window={window}
            wallModel={wallModel}
            onWallUpdate={onWallUpdate}
            onClick={() => onWindowClick(index, idx)}
            wallIndex={index}
            windowIndex={idx}
            selectedWindow={selectedWindow && selectedWindow.wallIndex === index && selectedWindow.windowIndex === idx ? selectedWindow : ({} as any)}
          />
        ))}

      {wallModel &&
        (wallModel.doors || []).map((door, idx) => (
          <MemoizedDoor
            key={`pw-door-${idx}`}
            door={door}
            wallModel={wallModel}
            wallIndex={index}
            doorIndex={idx}
            onWallUpdate={onWallUpdate}
            onClick={() => onDoorClick(index, idx)}
            selectedDoor={selectedDoor && selectedDoor.wallIndex === index && selectedDoor.doorIndex === idx ? selectedDoor : ({} as any)}
          />
        ))}

      {wallModel &&
        (wallModel.passages || []).map((passage, idx) => (
          <MemoizedPassage
            key={`pw-passage-${idx}`}
            passage={passage}
            wallModel={wallModel}
            wallIndex={index}
            passageIndex={idx}
            onWallUpdate={onWallUpdate}
            onClick={() => onPassageClick(index, idx)}
            selectedPassage={selectedPassage && selectedPassage.wallIndex === index && selectedPassage.passageIndex === idx ? selectedPassage : ({} as any)}
          />
        ))}

      {wallModel &&
        (['A', 'B'] as const).map((side) =>
          (wallModel.sides?.[side]?.radiators || []).map((radiator, idx) => (
            <MemoizedRadiator
              key={`pw-rad-${side}-${idx}`}
              radiator={radiator}
              wallModel={wallModel}
              side={side}
              radiatorIndex={idx}
              wallIndex={index}
              onClick={() => onRadiatorClick(index, idx, side)}
              onWallUpdate={onWallUpdate}
              selected={!!selectedRadiator && selectedRadiator.wallIndex === index && selectedRadiator.radiatorIndex === idx && selectedRadiator.side === side}
            />
          )),
        )}

      {/* NEU: ElementDistanceIndicator auf das neue Prop-Format umgestellt */}
      {wallModel &&
        showElementDistances &&
        ((wallModel.doors?.length ?? 0) > 0 || (wallModel.windows?.length ?? 0) > 0 || (wallModel.passages?.length ?? 0) > 0) &&
        pC2 &&
        pD2 &&
        wallNormal && (
        <ElementDistanceIndicator 
          windows={wallModel.windows || []} 
          doors={wallModel.doors || []} 
          passages={wallModel.passages || []} 
          visualStartPoint={pC2.clone()} 
          visualEndPoint={pD2.clone()} 
          inwardNormal={wallNormal.clone().negate()} 
        />
      )}

      {pC2 && pD2 && wallLength > 0 && (hovered || isSelected || showWallInnerLength) && (
        <WallLengthIndicator
          startPoint={pC2.clone()}
          endPoint={pD2.clone()}
          wallSideALength={wallLength}
          shiftIndicator={showElementDistances && ((wallModel.doors?.length ?? 0) > 0 || (wallModel.windows?.length ?? 0) > 0 || (wallModel.passages?.length ?? 0) > 0)}
        />
      )}

      {!isDragging && wallModel && (
        <>
          <DraggablePoint
            key={`${index}start-${wallModel.startPoint.x}-${wallModel.startPoint.y}`}
            position={wallModel.startPoint}
            pointType={EDraggablePointType.START}
            onStartDragging={handleStartEndpointDrag}
            allowPropagation={false}
          />
          <DraggablePoint
            key={`${index}end-${wallModel.endPoint.x}-${wallModel.endPoint.y}`}
            position={wallModel.endPoint}
            pointType={EDraggablePointType.END}
            onStartDragging={handleStartEndpointDrag}
            allowPropagation={false}
          />
        </>
      )}
    </group>
  );
};

export default PartitionWall;
